<template>
  <div class="memberinvitation-form">
    <slot name="header"></slot>

    <div class="line_flex mb-4">
      <template v-if="roles">
        <input-dropdown
          v-model:value="data.role"
          :options="invitableRoles"
          :label="$t('labels.role')"
          item-key="roleCode"
          dropdown-class="dropdown-slim"
          class="min-w-10"
          prop="name"
        ></input-dropdown>
      </template>
      <input-text
        v-if="canSetValidUntil"
        v-model:value="validUntilDate"
        type="date"
        :label="memberWord == 'user' ? $t('labels.userValidUntil'): $t('labels.memberValidUntil')"
      />
    </div>

    <div v-if="knownUsersWithoutExistingEmails.length > 0" class="mb-4">
      <div class="label">{{ $t("headings.chooseFromUsers") }}</div>
      <users-picker
        :users="knownUsersWithoutExistingEmails"
        :selected-user-ids="selectedKnownUserIds"
        @check-user="addEntry($event.email)"
        @uncheck-user="removeEmail($event.email)"
      />
    </div>
    <input-email-list
      ref="emailInput"
      :emails="existingEmails"
      :added-emails="entryEmails"
      :max-emails="maxInvitations"
      :autocomplete-options="autocompleteOptions"
      @add="addEntry($event)"
    >
    </input-email-list>

    <form
      class="memberinvitation-form__form well"
      @submit.prevent="inviteMembers()"
    >
      <div v-show="data.entries.length > 0">
        <transition-group
          tag="table"
          class="memberinvitation-form__list"
          name="animation-list"
        >
          <tr :key="'headings'" class="memberinvitation-form__listheader">
            <th>{{ $t("labels.email") }}</th>
            <th>
              <span v-if="canSetValidUntil">
                <template v-if="memberWord === 'user'">
                  {{ $t("labels.userValidUntil") }}
                </template>
                <span v-else>
                  {{ $t("labels.memberValidUntil") }}
                </span>
              </span>
            </th>
            <th>
              <span v-if="roles">{{ $t("labels.role") }}</span>
            </th>
            <th></th>
          </tr>
          <tr
            v-for="(entry, i) in data.entries"
            :key="entry.user.email"
            class="memberinvitation-form__entry"
          >
            <td>
              <span>{{ entry.user.email }}</span>
              <span v-if="data.emailErrors[i]" class="message-error">
                <template v-if="data.emailErrors[i].user">
                  <span v-for="err in data.emailErrors[i].user" :key="err.code">
                    {{ err?.message ?? err }}
                  </span>
                </template>
                <template v-else>
                  {{ data.emailErrors[i] }}
                </template>
              </span>
            </td>
            <td>
              <div v-if="canSetValidUntil" class="whitespace-nowrap">
                <input-text
                  :value="toDateString(entry.validUntil)"
                  style="min-width: 10rem"
                  type="date"
                  @update:value="entry.validUntil = toDateTimeString($event)"
                />
              </div>
            </td>
            <td>
              <template v-if="entry.roleCode">
                <input-dropdown
                  :options="invitableRoles"
                  :value="rolesByCode[entry.roleCode]"
                  prop="name"
                  item-key="roleCode"
                  dropdown-class="dropdown-slim"
                  @update:value="
                    updateEntry(entry, { roleCode: $event.roleCode })
                  "
                >
                  <template #label></template>
                </input-dropdown>
              </template>
            </td>
            <td>
              <button
                class="button-icon"
                type="button"
                @click="removeEmail(entry.user.email)"
              >
                <lucide-icon name="x" />
              </button>
            </td>
          </tr>
        </transition-group>
      </div>
      <button v-if="submitButtonVisible" class="button mt-4" :disabled="data.entries.length < 1" type="submit">
        {{ $t("actions.inviteUsers") }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputEmailList from "@core/input/InputEmailList.vue";
import InputText from "@core/input/InputText.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import UsersPicker from "@manager/common/UsersPicker.vue";

import { useUser } from "@/api/user.store";
import { showError } from "@core/ui/alert/alert";
import { BadRequestError } from "@core/errors/Errors";

type ListEntry = {
  user: { email: string };
  validUntil?: string;
  roleCode?: string;
};

const props = withDefaults(defineProps<{
  inviteFunction: (entries: ListEntry[]) => Promise<unknown>;
  maxInvitations?: number;
  existingEmails?: string[];
  autocompleteOptions?: { value: string; label: string }[];
  roles?: { roleCode: string }[] | null;
  defaultRoleCode?: string;
  canSetValidUntil?: boolean;
  submitButtonVisible?: boolean;
  memberWord?: "member" | "user";
}>(), {
  maxInvitations: Infinity,
  existingEmails: () => [],
  autocompleteOptions: () => [],
  roles: null,
  defaultRoleCode: "MEMBER",
  canSetValidUntil: false,
  submitButtonVisible: true,
  memberWord: "member"
});

const emit = defineEmits<{
  (event: "invited"): void;
}>();

const { t } = useI18n();
const { getKnownUsers } = useUser();

const emailInput = ref<InstanceType<typeof InputEmailList>>();

const data = reactive({
  entries: [] as ListEntry[],
  emailErrors: {} as Record<
    string,
    Record<string, Record<string, { code: string; message: string }>>
  >,
  role: undefined as undefined | { roleCode: string },
  knownUsers: [] as { id: number; email: string; name: string | null }[],
  validUntil: null as null | string
});

const entryEmails = computed(() => {
  return data.entries.map((e) => e.user.email);
});

const allEmails = computed(() => {
  return entryEmails.value.concat(props.existingEmails);
});

const rolesByCode = computed(() => {
  const dict: Record<string, { roleCode: string }> = {};
  if (!props.roles) return dict;
  for (const role of props.roles) {
    dict[role.roleCode] = role;
  }
  return dict;
});

const invitableRoles = computed(() => {
  if (!props.roles) return [];
  return props.roles.filter((r) => r.roleCode != "OWNER");
});

const knownUsersWithoutExistingEmails = computed(() => {
  const users = data.knownUsers.filter(
    (u) => !props.existingEmails.includes(u.email)
  );

  users.sort((a, b) => {
    if (a.email > b.email) return 1;
    else return -1;
  });

  return users;
});

const selectedKnownUserIds = computed(() => {
  return knownUsersWithoutExistingEmails.value
    .filter((u) => entryEmails.value.includes(u.email))
    .map((u) => u.id);
});

const validUntilDate = computed({
  get() {
    return data.validUntil ? data.validUntil.substring(0, 10) : undefined;
  },
  set(value: string | undefined) {
    if (value == null) data.validUntil = null;
    else if (value == "") data.validUntil = null;
    else {
      data.validUntil = new Date(value).toISOString();
    }
  }
});

watch(() => props.defaultRoleCode, (value) => {
  data.role = invitableRoles.value.find((r) => r.roleCode == value);
}, {
  immediate: true
});

watch(() => props.roles, () => {
  data.role = invitableRoles.value.find(
    (r) => r.roleCode == props.defaultRoleCode
  );
}, {
  immediate: true
});

watch(() => data.entries, () => {
  data.emailErrors = {};
});

onMounted(() => {
  getKnownUsers()
    .then((users) => (data.knownUsers = users))
    .catch((error) => {
      console.warn("error getting known users", error);
    });
});


function addEntry(email: string) {
  if (allEmails.value.length >= props.maxInvitations) {
    showError(t("errors.maxEmailsReached"));
    return false;
  }
  const entry: {
    user: { email: string };
    validUntil?: string;
    roleCode?: string;
  } = {
    user: { email }
  };
  if (data.validUntil) {
    entry.validUntil = data.validUntil;
  }
  if (data.role) {
    entry.roleCode = data.role.roleCode;
  }
  data.entries.unshift(entry);
}

function updateEntry(entry: ListEntry, changes: Partial<ListEntry>) {
  Object.assign(entry, changes);
}

function removeEmail(email: string) {
  data.entries = data.entries.filter((entry) => entry.user.email != email);
}

async function inviteMembers() {
  // make sure the input is empty before submitting:
  const inputemail = emailInput.value;
  if (inputemail == null) return false;

  if (inputemail.submit() === false) {
    return false;
  }

  try {
    await props.inviteFunction(data.entries);
    data.entries = [];
    data.emailErrors = {};
    emit("invited");
    return true;
  } catch (error) {
    console.log("error", error);
    const fieldErrors = (error as BadRequestError)?.fields;
    if (fieldErrors) {
      data.emailErrors = fieldErrors as unknown as typeof data.emailErrors;
    }
    return false;
  }
}

function toDateString(datetimestring?: string) {
  if (datetimestring == null) return undefined;
  const d = new Date(datetimestring);
  return d.toISOString().substring(0, 10);
}

function toDateTimeString(dateString?: string) {
  if (dateString == null) return undefined;
  const d = new Date(dateString);
  return d.toISOString();
}

defineExpose({
  submit: inviteMembers,
  emailInput,
  data
});
</script>

<style lang="scss">
.memberinvitation-form {
  text-align: left;
  background-color: var(--color-background-deep);
  padding: 2rem;

  &__form {
    margin-top: 0.5rem;
  }

  &__list {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__listheader,
  &__entry {
    display: grid;
    grid-template-columns: 1fr 10rem 12rem 3rem;
    gap: 0.5rem;
    padding: 0 1rem;
  }

  &__listheader {
    font-size: var(--font-size-label);
    color: var(--color-text-medium);
  }

  &__entry {
    justify-content: space-between;
    align-items: center;
    background-color: var(--color-background-close);
    border-radius: 5px;
  }
}
</style>
